// src/modules/analytics/hooks/use-analytics.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  LlmUsageStatItem,
  LlmUsageStatByScenarioItem,
  AnalyticsFilters,
  AnalyticsKpi,
} from '../types';
import { getStats, getStatsByScenario } from '../api';
import { aggregateKpi } from '../utils';

function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateMinusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDefaultFilters(): AnalyticsFilters {
  return {
    date_from: getDateMinusDays(30),
    date_to: getTodayStr(),
    project_id: null,
    user_id: null,
  };
}

export interface UseAnalyticsResult {
  pendingFilters: AnalyticsFilters;
  setPendingFilters: (f: AnalyticsFilters) => void;
  appliedFilters: AnalyticsFilters;
  stats: LlmUsageStatItem[];
  byScenario: LlmUsageStatByScenarioItem[];
  loading: boolean;
  error: string | null;
  kpi: AnalyticsKpi;
  applyFilters: () => void;
}

export function useAnalytics(): UseAnalyticsResult {
  const [pendingFilters, setPendingFilters] = useState<AnalyticsFilters>(getDefaultFilters);
  // Применённые фильтры (после «Применить») — для зависимых блоков, например таблицы пользователей
  const [appliedFilters, setAppliedFilters] = useState<AnalyticsFilters>(getDefaultFilters);
  const [stats, setStats] = useState<LlmUsageStatItem[]>([]);
  const [byScenario, setByScenario] = useState<LlmUsageStatByScenarioItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (filters: AnalyticsFilters) => {
    setAppliedFilters(filters);
    setLoading(true);
    setError(null);
    try {
      const [statsData, scenarioData] = await Promise.all([
        getStats(filters),
        getStatsByScenario(filters),
      ]);
      setStats(statsData);
      setByScenario(scenarioData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  // Используем ref чтобы избежать stale closure в useEffect
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    loadRef.current(getDefaultFilters());
  }, []); // только при монтировании

  const applyFilters = useCallback(() => {
    load(pendingFilters);
  }, [pendingFilters, load]);

  const kpi: AnalyticsKpi = aggregateKpi(stats);

  return {
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    stats,
    byScenario,
    loading,
    error,
    kpi,
    applyFilters,
  };
}
